import { useEffect, useState } from 'react';
import {
  chatStyles,
  chatThemeSchema,
  cornerStyles,
  supportedFonts,
  type ChatTheme,
} from '@kairelo/shared';
import { profile as profileApi } from '../lib/api.js';
import { ThemedRegion } from '../components/ThemeStyle.tsx';
import { Icon } from '../components/Icon.tsx';
import { Button, Card, Field, Loading, Notice, useToast } from '../components/ui.tsx';

/**
 * Structured chat branding (concept §10).
 *
 * Colours, a supported font, a visual style and a corner treatment, applied to
 * what both parties see. Every control is a validated colour or a value from a
 * fixed list — there is no CSS field, because arbitrary custom CSS is out of V1,
 * and no custom-domain or white-label setting, because those are Phase 3.
 */

const FONT_LABELS: Record<(typeof supportedFonts)[number], string> = {
  system: 'System default',
  inter: 'Inter',
  'ibm-plex-sans': 'IBM Plex Sans',
  'ibm-plex-mono': 'IBM Plex Mono',
  newsreader: 'Newsreader (serif)',
  georgia: 'Georgia (serif)',
};

const STYLE_LABELS: Record<(typeof chatStyles)[number], string> = {
  classic: 'Classic',
  soft: 'Soft',
  compact: 'Compact',
  contrast: 'High contrast',
};

const CORNER_LABELS: Record<(typeof cornerStyles)[number], string> = {
  sharp: 'Sharp',
  rounded: 'Rounded',
  pill: 'Pill',
};

const PRESETS: Array<{ name: string; theme: Partial<ChatTheme> }> = [
  { name: 'Kairelo', theme: { accentColour: '#246bfd', backgroundColour: '#f3f7fa' } },
  { name: 'Teal', theme: { accentColour: '#0c766f', backgroundColour: '#eafbf7' } },
  { name: 'Indigo', theme: { accentColour: '#4338ca', backgroundColour: '#f3f4f8' } },
  { name: 'Plum', theme: { accentColour: '#7a3ea3', backgroundColour: '#f7f3f8' } },
  { name: 'Ember', theme: { accentColour: '#b3452a', backgroundColour: '#faf4f1' } },
  { name: 'Slate', theme: { accentColour: '#334155', backgroundColour: '#f4f5f7' } },
];

export function BrandingEditor() {
  const toast = useToast();
  const [theme, setTheme] = useState<ChatTheme>(chatThemeSchema.parse({}));
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    profileApi
      .get()
      .then((profile) => {
        setTheme(profile.theme);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const update = <K extends keyof ChatTheme>(field: K, value: ChatTheme[K]) =>
    setTheme((previous) => ({ ...previous, [field]: value }));

  if (!loaded) {
    return (
      <div className="page">
        <Loading>Loading your branding…</Loading>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__header">
        <span className="tile tile--accent" aria-hidden="true">
          <Icon name="palette" size={20} />
        </span>
        <div className="page__title">
          <h1>Branding</h1>
          <p>Applies to what both you and your visitor see in a conversation.</p>
        </div>
        <span className="spacer" />
        <Button
          variant="primary"
          icon="check"
          busy={saving}
          onClick={() => {
            setSaving(true);
            void profileApi
              .updateTheme(theme)
              .then(() => toast('success', 'Branding saved. Both participants will see it.'))
              .catch(() => toast('error', 'Branding could not be saved.'))
              .finally(() => setSaving(false));
          }}
        >
          Save branding
        </Button>
      </header>

      <Card title="Preview" description="Exactly how a conversation will look.">
        <ThemedRegion theme={theme}>
          <div
            style={{
              background: 'var(--brand-background)',
              color: 'var(--brand-text)',
              fontFamily: 'var(--brand-font)',
              padding: 'var(--s-4)',
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--border)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--s-1)',
            }}
          >
            <div className="bubble">Hello — thanks for making time.</div>
            <div className="bubble bubble--mine">
              Of course. This chat closes automatically in 24 hours.
            </div>
            <div className="bubble">Perfect. I&rsquo;ll send the file over now.</div>
          </div>
        </ThemedRegion>
      </Card>

      <Card title="Presets" description="A starting point you can adjust below.">
        <div className="row">
          {PRESETS.map((preset) => (
            <Button
              key={preset.name}
              size="sm"
              onClick={() => setTheme((previous) => ({ ...previous, ...preset.theme }))}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: preset.theme.accentColour,
                  display: 'inline-block',
                }}
              />
              {preset.name}
            </Button>
          ))}
        </div>
      </Card>

      <Card title="Colours">
        <div className="grid-2">
          <ColourField
            id="accent"
            label="Accent"
            value={theme.accentColour}
            onChange={(value) => update('accentColour', value)}
          />
          <ColourField
            id="background"
            label="Background"
            value={theme.backgroundColour}
            onChange={(value) => update('backgroundColour', value)}
          />
          <ColourField
            id="surface"
            label="Bubbles and panels"
            value={theme.surfaceColour}
            onChange={(value) => update('surfaceColour', value)}
          />
          <ColourField
            id="text"
            label="Text"
            value={theme.textColour}
            onChange={(value) => update('textColour', value)}
          />
        </div>
      </Card>

      <Card title="Type and shape">
        <div className="grid-2">
          <Field
            label="Font"
            htmlFor="font"
            hint="Supported fonts only; nothing is loaded from a CDN."
          >
            {({ id, describedBy }) => (
              <select
                id={id}
                value={theme.font}
                aria-describedby={describedBy}
                onChange={(event) => update('font', event.target.value as ChatTheme['font'])}
              >
                {supportedFonts.map((font) => (
                  <option key={font} value={font}>
                    {FONT_LABELS[font]}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Overall style" htmlFor="style">
            {({ id }) => (
              <select
                id={id}
                value={theme.style}
                onChange={(event) => update('style', event.target.value as ChatTheme['style'])}
              >
                {chatStyles.map((style) => (
                  <option key={style} value={style}>
                    {STYLE_LABELS[style]}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Corners" htmlFor="corners">
            {({ id }) => (
              <select
                id={id}
                value={theme.corners}
                onChange={(event) => update('corners', event.target.value as ChatTheme['corners'])}
              >
                {cornerStyles.map((corner) => (
                  <option key={corner} value={corner}>
                    {CORNER_LABELS[corner]}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
      </Card>

      <Notice title="Structured settings only">
        Kairelo does not accept custom CSS. Custom domains and full white-label presentation are not
        part of this release.
      </Notice>
    </div>
  );
}

function ColourField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label} htmlFor={id}>
      {({ id: fieldId }) => (
        <span className="swatch">
          <input
            id={fieldId}
            type="color"
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
          <code>{value}</code>
        </span>
      )}
    </Field>
  );
}
